import React, { FC } from 'react';
import styled, { css, DefaultTheme, FlattenInterpolation, ThemeProps } from 'styled-components';
import { header6, plainTextStyle } from '@westwing/ui-kit/typography';
import { ScreenType, forDesktop } from '@westwing/ui-kit/breakPoints';
import HybridImage from '../Generic/HybridImage';
import HybridLink from '../Generic/Links/HybridLink';
import useGa from '../../hooks/useGa';
import { BannerTrackingVerations } from '../../ga/homepage';
import GenericImpressionWrapper from '../Generic/GenericImpressionWrapper';

export type Typography = FlattenInterpolation<ThemeProps<DefaultTheme>>;

interface ImgProps {
    title: string;
    targetUrl: string;
    mobileImage: string;
    desktopImage: string;
    className?: string;
    trackingName: string;
}

const SecondaryBannerImgInner = styled(HybridImage)`
    display: block;
    position: relative;
    &:before {
        display: block;
        content: '';
        width: 100%;
        padding-top: 133%;
        ${forDesktop`
            padding-top: 75%;
        `}
    }
    img {
        position: absolute;
        top: 0;
        right: 0;
        bottom: 0;
        left: 0;
    }
`;

export const SecondaryBannerImg: FC<ImgProps> = ({
    title,
    mobileImage,
    desktopImage,
    targetUrl,
    className,
    trackingName,
}) => {
    const gaTracking = useGa();
    const imgProps = {
        desktopSrc: desktopImage,
        fallbackSrc: desktopImage,
        mobileSrc: mobileImage,
        alt: title,
        breakPoint: 'breakpointDesktop' as ScreenType,
    };

    if (targetUrl) {
        const trackingFn = gaTracking.homepage.getBannerTrackingFn(BannerTrackingVerations.General, trackingName);
        return (
            <HybridLink to={targetUrl} className={className} onClick={trackingFn}>
                <GenericImpressionWrapper action={() => gaTracking.homepage.trackBannerImpression(trackingName)}>
                    <SecondaryBannerImgInner {...imgProps} />
                </GenericImpressionWrapper>
            </HybridLink>
        );
    }
    return (
        <GenericImpressionWrapper
            className={className}
            action={() => gaTracking.homepage.trackBannerImpression(trackingName)}
        >
            <HybridImage {...imgProps} />
        </GenericImpressionWrapper>
    );
};

const Title = styled.h2<{ desktopTypography?: Typography }>`
    ${header6}
    ${({ desktopTypography }) =>
        desktopTypography
            ? css`
                  ${forDesktop`
                    ${desktopTypography};
                `}
              `
            : ''}
    line-height: 25px;
    margin-bottom: 0;
    color: ${({ theme }) => theme.colors.charcoal100};
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
`;

const SubTitle = styled.p`
    ${plainTextStyle}
    line-height: 19.6px;
    margin: 3px 0 5px;
    color: ${({ theme }) => theme.colors.charcoal100};
`;

const Cta = styled.p`
    ${plainTextStyle}
    line-height: 14px;
    text-decoration: underline;
    margin: 0;
    padding-top: 2px;
    color: ${({ theme }) => theme.colors.charcoal100};
`;

const BannerTextWrapper = styled.div`
    padding-bottom: 40px;
`;

interface TextProps {
    title: string;
    subtitle?: string;
    ctaText: string;
    targetUrl: string;
    className?: string;
    desktopTypography?: Typography;
    trackingName: string;
}

const SecondaryBannerTextInner: FC<{
    title: string;
    subTitle?: string;
    ctaText: string;
    desktopTypography?: Typography;
}> = ({ title, subTitle, ctaText, desktopTypography }) => (
    <>
        <Title desktopTypography={desktopTypography}>{title}</Title>
        {subTitle ? <SubTitle>{subTitle}</SubTitle> : ''}
        <Cta>{ctaText}</Cta>
    </>
);

export const BannerText: FC<TextProps> = ({
    title,
    subtitle,
    ctaText,
    targetUrl,
    className,
    desktopTypography,
    trackingName,
}) => {
    const gaTracking = useGa();
    return (
        <BannerTextWrapper className={className}>
            {targetUrl ? (
                <HybridLink
                    to={targetUrl}
                    onClick={gaTracking.homepage.getBannerTrackingFn(BannerTrackingVerations.General, trackingName)}
                >
                    <SecondaryBannerTextInner
                        title={title}
                        subTitle={subtitle}
                        ctaText={ctaText}
                        desktopTypography={desktopTypography}
                    />
                </HybridLink>
            ) : (
                <div className={className}>
                    <SecondaryBannerTextInner title={title} subTitle={subtitle} ctaText={ctaText} />
                </div>
            )}
        </BannerTextWrapper>
    );
};
